<template>
  <div class="st-page">
    <Navigation />
    <PhotoLightbox :src="lightbox.src" :caption="lightbox.caption" @close="closeLightbox" />
    <div class="container" style="padding-top:20px;">
      <div class="d-flex align-items-center justify-content-between mb-4">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
          <div><h4 class="st-page-title"><i class="mdi mdi-account-school me-2"></i>Students</h4></div>
        </div>
        <div class="d-flex gap-2">
          <button class="st-btn st-btn-blue me-2" @click="openFindDupes" title="Find and resolve duplicate student registrations"><i class="mdi mdi-account-multiple-remove me-1"></i>Find Duplicates</button>
          <button class="st-btn st-btn-blue me-2" @click="$router.push('/parents')" title="View all parents in one place"><i class="mdi mdi-account-multiple me-1"></i>All Parents</button>
          <button class="st-btn st-btn-blue me-2" @click="resendAllParentWelcomes" :disabled="resendingBulk" title="Resend the welcome SMS (with parent-portal link) to every parent in this school">
            <span v-if="resendingBulk" class="spinner-border spinner-border-sm me-1"></span><i v-else class="mdi mdi-message-text-fast me-1"></i>{{ resendingBulk ? 'Sending...' : 'Resend Parent Welcomes' }}
          </button>
          <button class="st-btn st-btn-blue" @click="openAddStudent"><i class="mdi mdi-plus me-1"></i>Add Student</button>
        </div>
      </div>

      <!-- Filters -->
      <div class="d-flex flex-wrap gap-2 mb-3">
        <div class="st-search-wrap"><i class="mdi mdi-magnify"></i><input class="st-search" v-model="search" placeholder="Search name or admission no..." @input="onFilterChange" /></div>
        <select class="st-select" style="width:auto;padding:7px 12px;font-size:13px;" v-model="filterClass" @change="onFilterChange">
          <option value="">All Classes</option>
          <option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
        <select class="st-select" style="width:auto;padding:7px 12px;font-size:13px;" v-model="filterStatus" @change="onFilterChange">
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="graduated">Graduated</option>
          <option value="">All</option>
        </select>
        <select class="st-select" style="width:auto;padding:7px 12px;font-size:13px;" v-model="pageSize" @change="onPageSizeChange" title="Rows per page">
          <option :value="50">50 / page</option>
          <option :value="100">100 / page</option>
          <option :value="200">200 / page</option>
          <option value="all">Show all</option>
        </select>
      </div>

      <div v-if="loading" class="st-spinner"><div class="spinner-border"></div></div>
      <div v-else>
        <div class="st-card">
          <div class="d-flex justify-content-between mb-2">
            <span style="color:#8BA4C4;font-size:13px;">{{ total }} students</span>
          </div>
          <div style="overflow-x:auto;" v-if="students.length">
            <table class="st-table">
              <thead><tr><th style="width:60px;">Photo</th><th>Name</th><th>Adm No</th><th>Class</th><th>Gender</th><th>Wallet</th><th>Parents</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
              <tr v-for="s in students" :key="s.id">
                <td>
                  <div class="st-avatar" @click="s.photo_url ? openLightbox(s) : (canEditStudents && openEditStudent(s))" :title="s.photo_url ? 'Click to enlarge' : (canEditStudents ? 'Click to add photo' : '')">
                    <img v-if="s.photo_url" :src="s.photo_url" :alt="s.name" loading="lazy" />
                    <span v-else class="st-avatar-fallback">{{ avatarInitials(s.name) }}</span>
                  </div>
                </td>
                <td style="font-weight:600;">{{ s.name }}</td>
                <td>{{ s.admission_no }}</td>
                <td>{{ s.class_name || '-' }}</td>
                <td>{{ s.gender || '-' }}</td>
                <td style="color:#4FC3F7;font-weight:600;">KSh {{ fNum(s.wallet_balance) }}</td>
                <td><span class="st-badge st-badge-info">{{ s.parent_count || 0 }}</span></td>
                <td><span class="st-badge" :class="s.status==='active'?'st-badge-success':'st-badge-warning'">{{ s.status }}</span></td>
                <td>
                  <button class="st-btn st-btn-sm st-btn-blue me-1" @click="viewStudent(s)" title="View"><i class="mdi mdi-eye"></i></button>
                  <button v-if="canEditStudents" class="st-btn st-btn-sm me-1" style="background:rgba(245,158,11,.15);color:#F59E0B;border:1px solid rgba(245,158,11,.3);" @click="openEditStudent(s)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                  <button class="st-btn st-btn-sm st-btn-green me-1" @click="openAddParent(s)" title="Add Parent"><i class="mdi mdi-account-plus"></i></button>
                  <button v-if="canDeleteStudents" class="st-btn st-btn-sm" :disabled="deletingId === s.id" style="background:rgba(239,68,68,.15);color:#EF4444;border:1px solid rgba(239,68,68,.3);" @click="confirmDeleteStudent(s)" title="Delete student">
                    <i v-if="deletingId !== s.id" class="mdi mdi-trash-can"></i>
                    <span v-else class="spinner-border spinner-border-sm"></span>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="st-empty"><i class="mdi mdi-account-school"></i>No students found</div>

          <!-- Pagination footer -->
          <div v-if="students.length && pageSize !== 'all'" class="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3" style="border-top:1px solid #1A3A6A;padding-top:12px;">
            <div style="color:#8BA4C4;font-size:12px;">
              Showing <strong style="color:#E8EDF4;">{{ ((page - 1) * pageSize) + 1 }}</strong>
              –<strong style="color:#E8EDF4;">{{ Math.min(page * pageSize, total) }}</strong>
              of <strong style="color:#E8EDF4;">{{ total }}</strong>
            </div>
            <div class="d-flex align-items-center gap-2">
              <button class="st-btn st-btn-sm" :disabled="page <= 1" @click="goToPage(1)" title="First page">«</button>
              <button class="st-btn st-btn-sm" :disabled="page <= 1" @click="goToPage(page - 1)" title="Previous">‹ Prev</button>
              <span style="color:#E8EDF4;font-size:13px;font-weight:600;padding:0 8px;">
                Page <input type="number" min="1" :max="totalPages" v-model.number="page" @change="goToPage(page)" style="width:55px;background:#0A1628;border:1px solid #1A3A6A;border-radius:6px;color:#E8EDF4;padding:4px 6px;text-align:center;font-size:12px;" />
                / {{ totalPages }}
              </span>
              <button class="st-btn st-btn-sm" :disabled="page >= totalPages" @click="goToPage(page + 1)" title="Next">Next ›</button>
              <button class="st-btn st-btn-sm" :disabled="page >= totalPages" @click="goToPage(totalPages)" title="Last page">»</button>
            </div>
          </div>
          <div v-else-if="students.length && pageSize === 'all'" class="d-flex justify-content-end mt-3" style="border-top:1px solid #1A3A6A;padding-top:12px;">
            <span style="color:#8BA4C4;font-size:12px;">Showing all <strong style="color:#E8EDF4;">{{ total }}</strong> students</span>
          </div>
        </div>
      </div>

      <!-- ============ ADD STUDENT MODAL ============ -->
      <div class="st-modal-overlay" v-if="showAdd" @click.self="showAdd=false">
        <div class="st-modal" style="max-width:600px;">
          <div class="st-modal-title"><span><i class="mdi mdi-account-plus me-2"></i>Add Student</span><button class="st-modal-close" @click="showAdd=false">&times;</button></div>
          <div class="st-alert st-alert-danger" v-if="addError && !duplicateMatches.length">{{ addError }}</div>

          <!-- Duplicate match panel — appears when the server flags a possible dupe -->
          <div v-if="duplicateMatches.length" style="background:rgba(245,158,11,.08);border:1px solid rgba(245,158,11,.4);border-radius:10px;padding:12px 14px;margin-bottom:14px;">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
              <i class="mdi mdi-alert" style="color:#F59E0B;font-size:20px;"></i>
              <div style="color:#F59E0B;font-weight:700;font-size:13px;">Possible duplicate detected</div>
            </div>
            <div style="color:#8BA4C4;font-size:12px;margin-bottom:10px;">{{ addError }}</div>
            <div style="background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:8px 10px;margin-bottom:10px;max-height:200px;overflow:auto;">
              <div v-for="d in duplicateMatches" :key="d.id" style="padding:6px 0;border-bottom:1px dashed rgba(79,195,247,.1);">
                <div style="color:#E8EDF4;font-weight:600;font-size:13px;">{{ d.name }}</div>
                <div style="color:#8BA4C4;font-size:11px;">{{ d.admission_no }} · {{ d.class_name || 'no class' }} · {{ d.gender || '—' }} · {{ d.status }}</div>
              </div>
            </div>
            <div class="d-flex gap-2">
              <button class="btn st-btn-blue flex-fill" @click="duplicateMatches = []; addError = ''">← Cancel & review the form</button>
              <button class="btn" style="background:rgba(245,158,11,.15);color:#F59E0B;border:1px solid rgba(245,158,11,.4);border-radius:10px;padding:10px 18px;flex:1;" @click="addStudent(true)" :disabled="processing">
                <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>Register anyway
              </button>
            </div>
          </div>

          <!-- Student Info -->
          <div class="st-section-label mb-2">STUDENT INFORMATION</div>
          <div class="row g-3 mb-3">
            <div class="col-6"><label class="st-label">Full Name *</label><input class="st-input" v-model="form.name" placeholder="Student full name" /></div>
            <div class="col-6"><label class="st-label">Admission No</label><input class="st-input" v-model="form.admissionNo" placeholder="Auto-generated if empty" /></div>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-4"><label class="st-label">Date of Birth</label><input type="date" class="st-input st-date-input" v-model="form.dateOfBirth" max="2020-12-31" /></div>
            <div class="col-4"><label class="st-label">Gender</label>
              <select class="st-select" v-model="form.gender"><option value="">Select...</option><option value="male">Male</option><option value="female">Female</option></select>
            </div>
            <div class="col-4"><label class="st-label">Class</label>
              <select class="st-select" v-model="form.gradeId"><option value="">Select...</option><option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option></select>
            </div>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-6"><label class="st-label">Student Phone <span style="color:#8BA4C4;font-weight:400;">(optional — for campus/uni students)</span></label><input class="st-input" v-model="form.phone" placeholder="e.g. 0712345678" /></div>
            <div class="col-6"><label class="st-label">Student Email <span style="color:#8BA4C4;font-weight:400;">(optional)</span></label><input class="st-input" v-model="form.email" type="email" placeholder="e.g. student@school.ac.ke" /></div>
          </div>

          <!-- Parents / Guardians -->
          <div class="d-flex align-items-center justify-content-between mb-2 mt-4">
            <div class="st-section-label">PARENTS / GUARDIANS</div>
            <button class="st-btn st-btn-sm st-btn-blue" @click="addParentRow" v-if="form.parents.length < 4"><i class="mdi mdi-plus me-1"></i>Add Parent</button>
          </div>

          <div v-for="(p, pi) in form.parents" :key="pi" class="st-parent-card">
            <div class="d-flex align-items-center justify-content-between mb-2">
            <span class="st-parent-label">
              <i class="mdi mdi-account me-1"></i>
              {{ pi === 0 ? 'Primary Parent/Guardian' : 'Parent/Guardian ' + (pi + 1) }}
            </span>
              <button v-if="pi > 0" class="st-btn-icon" @click="removeParentRow(pi)" title="Remove"><i class="mdi mdi-close"></i></button>
            </div>
            <div class="row g-2">
              <div class="col-sm-6"><label class="st-label">Name *</label><input class="st-input" v-model="p.name" placeholder="Parent/Guardian name" /></div>
              <div class="col-sm-6"><label class="st-label">Phone *</label><input class="st-input" v-model="p.phone" placeholder="0712345678" /></div>
            </div>
            <div class="row g-2 mt-1">
              <div class="col-sm-6"><label class="st-label">Email</label><input class="st-input" v-model="p.email" placeholder="parent@email.com" /></div>
              <div class="col-sm-6"><label class="st-label">Relationship</label>
                <select class="st-select" v-model="p.relationship">
                  <option value="father">Father</option><option value="mother">Mother</option>
                  <option value="guardian">Guardian</option><option value="grandparent">Grandparent</option>
                  <option value="uncle">Uncle</option><option value="aunt">Aunt</option>
                  <option value="sibling">Sibling</option><option value="other">Other</option>
                </select>
              </div>
            </div>
            <div class="d-flex gap-3 mt-2">
              <label class="st-check-inline"><input type="checkbox" v-model="p.receivesSms" :true-value="1" :false-value="0" /> <span>Receive SMS</span></label>
              <label class="st-check-inline"><input type="checkbox" v-model="p.receivesEmail" :true-value="1" :false-value="0" /> <span>Receive Email</span></label>
            </div>
          </div>

          <button class="btn st-btn-primary w-100 mt-3" @click="addStudent" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : 'Save Student' }}
          </button>
        </div>
      </div>

      <!-- ============ EDIT STUDENT MODAL ============ -->
      <div class="st-modal-overlay" v-if="showEdit" @click.self="showEdit=false">
        <div class="st-modal" style="max-width:600px;">
          <div class="st-modal-title">
            <span><i class="mdi mdi-pencil me-2"></i>Edit Student</span>
            <button class="st-modal-close" @click="showEdit=false">&times;</button>
          </div>
          <div class="st-alert st-alert-danger" v-if="editError">{{ editError }}</div>
          <div class="st-alert st-alert-success" v-if="editSuccess">{{ editSuccess }}</div>

          <!-- Photo section — opens cropper modal on file pick. Saves as 5:6 portrait
               (matches the card's 22mm × 27mm photo box) so it always fits the card. -->
          <div class="d-flex align-items-center gap-3 mb-3" style="background:rgba(46,134,222,.05);border:1px solid #1A3A6A;border-radius:10px;padding:12px;">
            <div style="width:75px;height:90px;border-radius:8px;overflow:hidden;background:#0A1628;border:2px solid #1A3A6A;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
              <img v-if="editForm.photoUrl" :src="editForm.photoUrl + '?v=' + photoVersion" style="width:100%;height:100%;object-fit:cover;" alt="" />
              <span v-else style="color:#4A6A8A;font-size:24px;"><i class="mdi mdi-account"></i></span>
            </div>
            <div style="flex:1;">
              <div style="color:#E8EDF4;font-size:13px;font-weight:600;margin-bottom:4px;">ID Card Photo</div>
              <div style="color:#8BA4C4;font-size:11px;margin-bottom:6px;">Portrait, will be cropped to fit the card (5:6 ratio).</div>
              <div class="d-flex gap-2 flex-wrap">
                <button class="btn st-btn-sm st-btn-blue" @click="$refs.editPhotoFile.click()" :disabled="photoUploading">
                  <span v-if="photoUploading" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="mdi mdi-image-outline me-1"></i>{{ editForm.photoUrl ? 'Replace from file' : 'Upload from file' }}
                </button>
                <button class="btn st-btn-sm" style="background:rgba(168,85,247,.15);color:#A855F7;border:1px solid rgba(168,85,247,.4);" @click="openWebcam" :disabled="photoUploading">
                  <i class="mdi mdi-webcam me-1"></i>Use webcam
                </button>
              </div>
              <input ref="editPhotoFile" type="file" accept="image/*" capture="user" style="display:none;" @change="onEditPhotoSelected" />
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-md-7"><label class="st-label">Full Name *</label><input class="st-input" v-model="editForm.name" /></div>
            <div class="col-md-5"><label class="st-label">Admission No *</label><input class="st-input" v-model="editForm.admissionNo" /></div>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-md-4">
              <label class="st-label">Date of Birth</label>
              <input type="date" class="st-input" v-model="editForm.dateOfBirth" />
            </div>
            <div class="col-md-4">
              <label class="st-label">Gender</label>
              <select class="st-select" v-model="editForm.gender">
                <option value="">—</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="st-label">Blood Group</label>
              <select class="st-select" v-model="editForm.bloodGroup">
                <option value="">—</option>
                <option value="A+">A+</option><option value="A-">A-</option>
                <option value="B+">B+</option><option value="B-">B-</option>
                <option value="AB+">AB+</option><option value="AB-">AB-</option>
                <option value="O+">O+</option><option value="O-">O-</option>
              </select>
            </div>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-md-8">
              <label class="st-label">Class / Course *</label>
              <select class="st-select" v-model="editForm.currentGradeId">
                <option value="">— No class —</option>
                <option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
              <small style="color:#8BA4C4;font-size:11px;">Changing the class is logged in the student's movement history.</small>
            </div>
            <div class="col-md-4">
              <label class="st-label">Status</label>
              <select class="st-select" v-model="editForm.status">
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="alumni">Alumni</option>
                <option value="transferred">Transferred</option>
              </select>
            </div>
          </div>
          <div class="d-flex gap-2">
            <button class="btn st-btn-primary flex-fill" @click="saveStudentEdit" :disabled="editProcessing">
              <span v-if="editProcessing" class="spinner-border spinner-border-sm me-1"></span>{{ editProcessing ? 'Saving...' : 'Save Changes' }}
            </button>
            <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="showEdit=false">Cancel</button>
          </div>
        </div>
      </div>

      <!-- ============ WEBCAM CAPTURE MODAL ============ -->
      <div class="st-modal-overlay" v-if="showWebcam" @click.self="closeWebcam">
        <div class="st-modal" style="max-width:560px;">
          <div class="st-modal-title">
            <span><i class="mdi mdi-webcam me-2"></i>Capture from Webcam</span>
            <button class="st-modal-close" @click="closeWebcam">&times;</button>
          </div>
          <div v-if="webcamError" class="st-alert st-alert-danger" style="margin-bottom:12px;">{{ webcamError }}</div>
          <div style="background:#000;border-radius:10px;overflow:hidden;display:flex;align-items:center;justify-content:center;min-height:300px;position:relative;">
            <video ref="webcamVideo" autoplay playsinline muted style="width:100%;max-width:480px;display:block;transform:scaleX(-1);"></video>
            <!-- 5:6 portrait guide overlay so the user frames their face correctly -->
            <div v-if="webcamReady" style="position:absolute;inset:0;pointer-events:none;display:flex;align-items:center;justify-content:center;">
              <div style="width:200px;height:240px;border:2px dashed rgba(79,195,247,.7);border-radius:8px;box-shadow:0 0 0 9999px rgba(0,0,0,.45);"></div>
            </div>
          </div>
          <div style="color:#8BA4C4;font-size:11px;text-align:center;margin-top:8px;">Position the face inside the dashed frame, then capture.</div>
          <div class="d-flex gap-2 mt-3">
            <button class="btn st-btn-primary flex-fill" @click="captureWebcam" :disabled="!webcamReady">
              <i class="mdi mdi-camera me-1"></i>Capture
            </button>
            <button class="btn" v-if="webcamDevices.length > 1" @click="cycleWebcamDevice" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 14px;" title="Switch camera">
              <i class="mdi mdi-camera-switch"></i>
            </button>
            <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="closeWebcam">Cancel</button>
          </div>
        </div>
      </div>

      <!-- ============ PHOTO CROPPER MODAL ============ -->
      <!-- Drag the box to reposition; slider resizes (5:6 aspect locked). Live preview at card size. -->
      <div class="st-modal-overlay" v-if="showCropper" @click.self="closeCropper">
        <div class="st-modal" style="max-width:720px;">
          <div class="st-modal-title">
            <span><i class="mdi mdi-crop me-2"></i>Crop Photo for ID Card</span>
            <button class="st-modal-close" @click="closeCropper">&times;</button>
          </div>
          <div style="color:#8BA4C4;font-size:12px;margin-bottom:12px;">Drag the box to reposition; use the slider to resize. The card photo is portrait (5:6).</div>

          <div class="d-flex gap-3 flex-wrap" style="justify-content:center;">
            <!-- Cropper canvas + overlay -->
            <div ref="cropStage" class="crop-stage" :style="{width: cropDisplay.w + 'px', height: cropDisplay.h + 'px'}"
                 @mousedown="onCropDragStart" @touchstart="onCropDragStart">
              <img v-if="cropImageSrc" :src="cropImageSrc" :style="{width: cropDisplay.w + 'px', height: cropDisplay.h + 'px'}" draggable="false" />
              <div class="crop-frame"
                   :style="{left: cropBox.x + 'px', top: cropBox.y + 'px', width: cropBox.w + 'px', height: cropBox.h + 'px'}">
                <div class="crop-frame-grid"></div>
              </div>
            </div>

            <!-- Live card preview -->
            <div style="display:flex;flex-direction:column;align-items:center;gap:6px;">
              <div style="color:#8BA4C4;font-size:11px;font-weight:600;letter-spacing:.5px;">CARD PREVIEW</div>
              <canvas ref="cropPreview" width="120" height="144" style="border:2px solid #4FC3F7;border-radius:6px;background:#0A1628;display:block;"></canvas>
              <div style="color:#4A6A8A;font-size:10px;">90 × 108 px on card</div>
            </div>
          </div>

          <div class="mt-3" style="padding:0 8px;">
            <label style="color:#8BA4C4;font-size:11px;font-weight:600;">Frame size</label>
            <input type="range" :min="cropMin" :max="cropMax" v-model.number="cropBox.h" @input="onCropResize" style="width:100%;accent-color:#4FC3F7;" />
          </div>

          <div class="d-flex gap-2 mt-3">
            <button class="btn st-btn-primary flex-fill" @click="acceptCrop" :disabled="photoUploading">
              <span v-if="photoUploading" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="mdi mdi-check me-1"></i>{{ photoUploading ? 'Uploading…' : 'Use this photo' }}
            </button>
            <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="closeCropper" :disabled="photoUploading">Cancel</button>
          </div>
        </div>
      </div>

      <!-- ============ VIEW STUDENT MODAL ============ -->
      <div class="st-modal-overlay" v-if="showView" @click.self="showView=false">
        <div class="st-modal" style="max-width:600px;">
          <div class="st-modal-title"><span><i class="mdi mdi-account me-2"></i>{{ viewData.name }}</span><button class="st-modal-close" @click="showView=false">&times;</button></div>

          <div class="d-flex align-items-center gap-3 mb-3" style="background:rgba(46,134,222,.05);border:1px solid #1A3A6A;border-radius:10px;padding:12px;">
            <div class="st-avatar st-avatar-lg" @click="viewData.photo_url ? openLightbox(viewData) : (canEditStudents && (showView=false, openEditStudent(viewData)))" :title="viewData.photo_url ? 'Click to enlarge' : (canEditStudents ? 'Click to add photo' : '')">
              <img v-if="viewData.photo_url" :src="viewData.photo_url" :alt="viewData.name" />
              <span v-else class="st-avatar-fallback">{{ avatarInitials(viewData.name) }}</span>
            </div>
            <div style="flex:1;">
              <div style="color:#E8EDF4;font-size:16px;font-weight:700;">{{ viewData.name }}</div>
              <div style="color:#8BA4C4;font-size:12px;">{{ viewData.admission_no }} · {{ viewData.class_name || 'No class' }}</div>
              <div v-if="canEditStudents" style="color:#4FC3F7;font-size:11px;margin-top:4px;cursor:pointer;" @click="showView=false; openEditStudent(viewData)">
                <i class="mdi mdi-camera"></i> {{ viewData.photo_url ? 'Replace photo' : 'Add photo' }}
              </div>
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-6"><div class="st-detail-label">Admission No</div><div class="st-detail-val">{{ viewData.admission_no }}</div></div>
            <div class="col-6"><div class="st-detail-label">Class</div><div class="st-detail-val">{{ viewData.class_name || '-' }}</div></div>
            <div class="col-6"><div class="st-detail-label">Gender</div><div class="st-detail-val">{{ viewData.gender || '-' }}</div></div>
            <div class="col-6"><div class="st-detail-label">Date of Birth</div><div class="st-detail-val">{{ viewData.date_of_birth || '-' }}</div></div>
            <div class="col-6"><div class="st-detail-label">Wallet Balance</div><div class="st-detail-val d-flex align-items-center gap-2" style="color:#4FC3F7;font-weight:700;">KSh {{ fNum(viewData.wallet_balance) }} <button class="st-btn st-btn-sm st-btn-outline" @click="printStatement(viewData)" style="font-size:11px;padding:2px 8px;">📄 Statement</button></div></div>
            <div class="col-6"><div class="st-detail-label">Phone</div><div class="st-detail-val">{{ viewData.phone || '-' }}</div></div>
            <div class="col-6"><div class="st-detail-label">Email</div><div class="st-detail-val">{{ viewData.email || '-' }}</div></div>
            <div class="col-6"><div class="st-detail-label">Status</div><div class="st-detail-val"><span class="st-badge" :class="viewData.status==='active'?'st-badge-success':'st-badge-warning'">{{ viewData.status }}</span></div></div>
          </div>

          <div class="d-flex align-items-center justify-content-between mt-4 mb-2">
            <div class="st-section-label">WALLET PIN</div>
          </div>
          <div class="st-parent-view-card" style="padding:12px;">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <div style="color:#10B981;font-size:13px;" v-if="viewData.pin_status==='set'">🔒 Wallet PIN is set</div>
                <div style="color:#F59E0B;font-size:13px;" v-else-if="viewData.pin_status==='pending'">📱 PIN sent via SMS — waiting for first use</div>
                <div style="color:#8BA4C4;font-size:13px;" v-else>⚠️ No wallet PIN set</div>
              </div>
              <div class="d-flex gap-2">
                <button class="st-btn st-btn-sm" style="background:#F59E0B;color:#0A1B36;" @click="resetPin(viewData)" :disabled="pinResetting">
                  <i class="mdi mdi-refresh me-1"></i>{{ pinResetting ? 'Sending...' : 'Reset PIN via SMS' }}
                </button>
              </div>
            </div>
            <div v-if="pinMessage" class="mt-2" :style="{color: pinSuccess ? '#10B981' : '#EF4444', fontSize:'13px'}">{{ pinMessage }}</div>
          </div>

          <!-- WALLET STATEMENT -->
          <div class="d-flex align-items-center justify-content-between mt-4 mb-2">
            <div class="st-section-label">WALLET STATEMENT</div>
            <div class="d-flex gap-2">
              <button class="st-btn st-btn-sm st-btn-blue" @click="loadStatement(viewData)" :disabled="stmtLoading">
                <i class="mdi mdi-refresh me-1"></i>{{ stmtLoading ? 'Loading...' : 'Load' }}
              </button>
              <button class="st-btn st-btn-sm" style="background:#10B981;color:#fff;" @click="printStatement(viewData)" v-if="stmtTransactions.length">
                <i class="mdi mdi-printer me-1"></i>Print
              </button>
            </div>
          </div>
          <div v-if="stmtTransactions.length" id="walletStatement">
            <div style="overflow-x:auto;">
              <table class="st-table" style="font-size:12px;">
                <thead><tr><th>Date</th><th>Type</th><th>Description</th><th style="text-align:right;">Amount</th><th style="text-align:right;">Balance</th></tr></thead>
                <tbody>
                <tr v-for="t in stmtTransactions" :key="t.id">
                  <td>{{ formatDate(t.created_at) }}</td>
                  <td><span class="st-badge" :class="t.type==='credit'?'st-badge-success':'st-badge-danger'" style="font-size:10px;">{{ t.type }}</span></td>
                  <td>{{ t.description || t.purpose || '-' }}</td>
                  <td style="text-align:right;font-weight:600;" :style="{color:t.type==='credit'?'#10B981':'#EF4444'}">{{ t.type==='credit'?'+':'-' }}KSh {{ fNum(t.amount) }}</td>
                  <td style="text-align:right;color:#4FC3F7;font-weight:600;">KSh {{ fNum(t.balance_after) }}</td>
                </tr>
                </tbody>
              </table>
            </div>
            <div style="color:#8BA4C4;font-size:12px;margin-top:4px;">{{ stmtTransactions.length }} transactions</div>
          </div>
          <div v-else-if="stmtLoaded" class="st-empty" style="padding:16px;"><i class="mdi mdi-cash-remove"></i> No transactions yet</div>

          <div class="d-flex align-items-center justify-content-between mt-4 mb-2">
            <div class="st-section-label">PARENTS / GUARDIANS ({{ viewParents.length }})</div>
            <button class="st-btn st-btn-sm st-btn-blue" @click="showView=false;openAddParent(viewData)"><i class="mdi mdi-plus me-1"></i>Add</button>
          </div>

          <div v-if="viewParentsLoading" class="st-spinner"><div class="spinner-border spinner-border-sm"></div></div>
          <div v-else-if="viewParents.length">
            <div v-for="p in viewParents" :key="p.id" class="st-parent-view-card">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <div class="st-parent-view-name">{{ p.parent_name }} <span class="st-badge st-badge-info" v-if="p.is_primary == 1" style="margin-left:6px;">Primary</span></div>
                  <div class="st-parent-view-detail"><i class="mdi mdi-phone me-1"></i>{{ p.phone }}</div>
                  <div class="st-parent-view-detail" v-if="p.email"><i class="mdi mdi-email me-1"></i>{{ p.email }}</div>
                  <div class="st-parent-view-detail"><i class="mdi mdi-account-heart me-1"></i>{{ p.relationship }}</div>
                </div>
                <div class="d-flex flex-column gap-1 align-items-end">
                  <span class="st-badge" :class="p.receives_sms==1?'st-badge-success':'st-badge-danger'" style="font-size:10px;">{{ p.receives_sms==1?'SMS ON':'SMS OFF' }}</span>
                  <span class="st-badge" :class="p.receives_email==1?'st-badge-success':'st-badge-danger'" style="font-size:10px;">{{ p.receives_email==1?'Email ON':'Email OFF' }}</span>
                  <button class="st-btn-icon" @click="resendParentWelcome(p)" :disabled="resendingId === p.id" title="Resend welcome SMS with parent-portal link" style="margin-top:4px;color:#4FC3F7;">
                    <i v-if="resendingId !== p.id" class="mdi mdi-send"></i>
                    <span v-else class="spinner-border spinner-border-sm"></span>
                  </button>
                  <button class="st-btn-icon" @click="removeParent(p.id)" title="Remove"><i class="mdi mdi-delete" style="color:#EF4444;"></i></button>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="st-empty" style="padding:20px;"><i class="mdi mdi-account-off" style="font-size:32px;"></i>No parents registered</div>
        </div>
      </div>

      <!-- ============ FIND DUPLICATES MODAL ============ -->
      <div class="st-modal-overlay" v-if="showDupes" @click.self="showDupes = false">
        <div class="st-modal" style="max-width:760px;">
          <div class="st-modal-title">
            <span><i class="mdi mdi-account-multiple-remove me-2"></i>Find &amp; Resolve Duplicate Students</span>
            <button class="st-modal-close" @click="showDupes = false">&times;</button>
          </div>

          <div v-if="dupesLoading" class="st-spinner"><div class="spinner-border"></div></div>
          <div v-else-if="!dupClusters.length" style="padding:30px 16px;text-align:center;color:#10B981;">
            <i class="mdi mdi-check-circle" style="font-size:48px;display:block;margin-bottom:8px;"></i>
            No duplicate students found.
          </div>
          <div v-else>
            <div style="font-size:12px;color:#8BA4C4;margin-bottom:12px;">
              {{ dupClusters.length }} cluster{{ dupClusters.length === 1 ? '' : 's' }} of duplicates found.
              For each cluster, pick the row to <strong style="color:#10B981;">KEEP</strong>. Everything else gets deleted (or archived if it has payments / wallet balance).
            </div>

            <div v-for="(c, ci) in dupClusters" :key="ci" style="background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:12px;margin-bottom:12px;">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
                <div>
                  <div style="color:#E8EDF4;font-weight:700;text-transform:capitalize;">{{ c.name }}</div>
                  <div style="color:#8BA4C4;font-size:11px;">{{ c.count }} records</div>
                </div>
              </div>
              <div style="overflow-x:auto;">
                <table class="st-table" style="font-size:12px;">
                  <thead>
                    <tr><th>Keep</th><th>Adm No</th><th>Class</th><th>Status</th><th>Parents</th><th>Wallet</th><th>Att.</th><th>Pmts</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="s in c.students" :key="s.id" :style="dupSelections[ci] === s.id ? 'background:rgba(16,185,129,.06);' : ''">
                      <td><input type="radio" :name="'dup-' + ci" :value="s.id" v-model="dupSelections[ci]" style="accent-color:#10B981;width:18px;height:18px;cursor:pointer;" /></td>
                      <td>{{ s.admission_no }}</td>
                      <td>{{ s.class_name || '—' }}</td>
                      <td><span class="st-badge" :class="s.status === 'active' ? 'st-badge-success' : 'st-badge-warning'" style="font-size:10px;">{{ s.status }}</span></td>
                      <td>{{ s.parent_count }}</td>
                      <td>{{ Number(s.wallet_balance) > 0 ? 'KSh ' + Number(s.wallet_balance).toLocaleString() : '—' }}</td>
                      <td>{{ s.attendance_count }}</td>
                      <td>{{ s.payment_count }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="d-flex gap-2 mt-3">
              <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;flex:1;" @click="showDupes = false">Cancel</button>
              <button class="btn st-btn-primary flex-fill" @click="resolveDupes" :disabled="dupResolving || !allClustersChosen">
                <span v-if="dupResolving" class="spinner-border spinner-border-sm me-1"></span>{{ dupResolving ? 'Resolving...' : 'Resolve all clusters' }}
              </button>
            </div>
            <div v-if="!allClustersChosen" style="color:#F59E0B;font-size:11px;margin-top:6px;">Pick a "Keep" row for every cluster before resolving.</div>
          </div>
        </div>
      </div>

      <!-- ============ ADD PARENT TO EXISTING STUDENT MODAL ============ -->
      <div class="st-modal-overlay" v-if="showAddParent" @click.self="showAddParent=false">
        <div class="st-modal">
          <div class="st-modal-title"><span><i class="mdi mdi-account-plus me-2"></i>Add Parent to {{ addParentStudent.name }}</span><button class="st-modal-close" @click="showAddParent=false">&times;</button></div>
          <div class="st-alert st-alert-danger" v-if="addParentError">{{ addParentError }}</div>
          <div class="mb-3"><label class="st-label">Parent/Guardian Name *</label><input class="st-input" v-model="newParent.parentName" placeholder="Full name" /></div>
          <div class="mb-3"><label class="st-label">Phone *</label><input class="st-input" v-model="newParent.phone" placeholder="0712345678" /></div>
          <div class="mb-3"><label class="st-label">Email</label><input class="st-input" v-model="newParent.email" placeholder="parent@email.com" /></div>
          <div class="mb-3"><label class="st-label">Relationship</label>
            <select class="st-select" v-model="newParent.relationship">
              <option value="father">Father</option><option value="mother">Mother</option>
              <option value="guardian">Guardian</option><option value="grandparent">Grandparent</option>
              <option value="uncle">Uncle</option><option value="aunt">Aunt</option>
              <option value="sibling">Sibling</option><option value="other">Other</option>
            </select>
          </div>
          <button class="btn st-btn-primary w-100" @click="addParentToStudent" :disabled="addingParent">
            <span v-if="addingParent" class="spinner-border spinner-border-sm me-1"></span>
            {{ addingParent ? 'Adding...' : 'Add Parent' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import PhotoLightbox from "@/components/simi-students/PhotoLightbox.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: "StudentManagement",
  components: { Navigation, PhotoLightbox },
  data() {
    return {
      loading: true,
      students: [],
      total: 0,
      classes: [],
      search: '',
      filterClass: '',
      filterStatus: 'active',
      page: 1,
      pageSize: 50,
      totalPages: 1,
      // Add student
      showAdd: false,
      processing: false,
      addError: '',
      form: {
        name: '', admissionNo: '', dateOfBirth: '', gender: '', gradeId: '', phone: '', email: '',
        parents: [{ name: '', phone: '', email: '', relationship: 'father', receivesSms: 1, receivesEmail: 1 }]
      },
      // View student
      showView: false,
      viewData: {},
      viewParents: [],
      viewParentsLoading: false,
      resendingBulk: false,
      resendingId: null,
      // Duplicate detection
      duplicateMatches: [],
      duplicateReason: '',
      // Find Duplicates tool
      showDupes: false,
      dupesLoading: false,
      dupClusters: [],
      dupSelections: {},     // clusterIndex → keepId
      dupResolving: false,
      // PIN management
      pinResetting: false,
      pinMessage: '',
      pinSuccess: false,
      // Wallet statement
      stmtLoading: false,
      stmtLoaded: false,
      stmtTransactions: [],
      // Add parent to existing
      showAddParent: false,
      addParentStudent: {},
      newParent: { parentName: '', phone: '', email: '', relationship: 'guardian' },
      addingParent: false,
      addParentError: '',
      // Edit student
      showEdit: false,
      editProcessing: false,
      editError: '',
      editSuccess: '',
      editForm: { studentId: null, name: '', admissionNo: '', dateOfBirth: '', gender: '', bloodGroup: '', currentGradeId: '', status: 'active', photoUrl: '' },
      // Photo cropper state
      showCropper: false,
      photoUploading: false,
      photoVersion: 0,                            // bust the img cache after upload
      cropImage: null,                            // native Image (full resolution)
      cropImageSrc: '',                           // data URL fed to <img>
      cropDisplay: { w: 0, h: 0, scale: 1 },      // image size on screen + scale to source
      cropBox: { x: 0, y: 0, w: 0, h: 0 },        // crop frame in display pixels (5:6)
      cropMin: 60, cropMax: 600,                  // height range for slider
      cropDrag: null,                             // {dx, dy} active drag offset
      // Lightbox state — click any avatar to zoom
      lightbox: { src: '', caption: '' },
      // Webcam capture state
      showWebcam: false,
      webcamReady: false,
      webcamError: '',
      webcamStream: null,
      webcamDevices: [],                          // list of available video inputs
      webcamDeviceIdx: 0,                         // current device index
      // Action permissions (loaded once)
      myActions: [],
      deletingId: null,
    };
  },
  mounted() { this.loadClasses(); this.loadStudents(); this.loadMyActions(); },
  computed: {
    canEditStudents() { return this.myActions.includes('students.update'); },
    canDeleteStudents() { return this.myActions.includes('students.delete'); },
    allClustersChosen() {
      if (!this.dupClusters.length) return false;
      for (let i = 0; i < this.dupClusters.length; i++) {
        if (!this.dupSelections[i]) return false;
      }
      return true;
    },
  },
  methods: {
    fNum(v) { if (!v) return '0'; return Number(v).toLocaleString('en-KE'); },
    avatarInitials(name) {
      if (!name) return '?';
      const parts = String(name).trim().split(/\s+/);
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    openLightbox(s) {
      if (!s || !s.photo_url) return;
      this.lightbox = { src: s.photo_url, caption: `${s.name} · ${s.admission_no}` + (s.class_name ? ` · ${s.class_name}` : '') };
    },
    closeLightbox() { this.lightbox = { src: '', caption: '' }; },

    async loadClasses() {
      try {
        var r = await executeJson({ function: 'getClasses' }, simitrack);
        if (r.data.success) this.classes = Array.isArray(r.data.data) ? r.data.data : (r.data.data.classes || []);
      } catch (e) {}
    },

    async loadMyActions() {
      try {
        var r = await executeJson({ function: 'getMyActions' }, simitrack);
        if (r.data.success) this.myActions = (r.data.data && r.data.data.actions) || [];
      } catch (e) { /* silent — UI just hides edit button */ }
    },

    openEditStudent(s) {
      this.showEdit = true;
      this.editError = '';
      this.editSuccess = '';
      this.editForm = {
        studentId: s.id,
        name: s.name || '',
        admissionNo: s.admission_no || '',
        dateOfBirth: s.date_of_birth || '',
        gender: s.gender || '',
        bloodGroup: s.blood_group || '',
        currentGradeId: s.current_grade_id || s.class_id || '',
        status: s.status || 'active',
        photoUrl: s.photo_url || '',
      };
      // Reference back to the row in this.students so we can update photo_url in place
      this.editStudentRef = s;
    },

    // ============ WEBCAM ============
    async openWebcam() {
      this.webcamError = '';
      this.webcamReady = false;
      this.showWebcam = true;
      try {
        // Pre-flight permission so devices come back with labels.
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        // Enumerate available cameras after we have permission so labels are populated.
        const all = await navigator.mediaDevices.enumerateDevices();
        this.webcamDevices = all.filter(d => d.kind === 'videoinput');
        // Use the device the browser picked by default; remember its idx so the cycle button works.
        const trackId = stream.getVideoTracks()[0]?.getSettings()?.deviceId;
        const idx = this.webcamDevices.findIndex(d => d.deviceId === trackId);
        this.webcamDeviceIdx = idx >= 0 ? idx : 0;
        this.attachWebcamStream(stream);
      } catch (e) {
        this.webcamError = 'Could not access camera: ' + (e.message || e.name || 'Permission denied. Allow camera in your browser settings.');
      }
    },
    async cycleWebcamDevice() {
      if (this.webcamDevices.length < 2) return;
      this.webcamDeviceIdx = (this.webcamDeviceIdx + 1) % this.webcamDevices.length;
      const id = this.webcamDevices[this.webcamDeviceIdx].deviceId;
      this.stopWebcamStream();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: id } }, audio: false });
        this.attachWebcamStream(stream);
      } catch (e) { this.webcamError = 'Could not switch camera.'; }
    },
    attachWebcamStream(stream) {
      this.webcamStream = stream;
      this.$nextTick(() => {
        const v = this.$refs.webcamVideo;
        if (!v) return;
        v.srcObject = stream;
        v.onloadedmetadata = () => { this.webcamReady = true; };
      });
    },
    stopWebcamStream() {
      if (this.webcamStream) {
        this.webcamStream.getTracks().forEach(t => { try { t.stop(); } catch(e){} });
        this.webcamStream = null;
      }
      this.webcamReady = false;
    },
    closeWebcam() {
      this.stopWebcamStream();
      this.showWebcam = false;
      this.webcamError = '';
    },
    captureWebcam() {
      const v = this.$refs.webcamVideo;
      if (!v || !v.videoWidth) return;
      // Render the current video frame to a canvas (mirrored so the photo matches the live preview).
      const c = document.createElement('canvas');
      c.width = v.videoWidth;
      c.height = v.videoHeight;
      const ctx = c.getContext('2d');
      ctx.translate(c.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(v, 0, 0, c.width, c.height);
      const src = c.toDataURL('image/jpeg', 0.95);
      const img = new Image();
      img.onload = () => {
        this.cropImage = img;
        this.cropImageSrc = src;
        this.closeWebcam();
        this.openCropperFor(img);
      };
      img.src = src;
    },

    // ============ PHOTO CROPPER ============
    onEditPhotoSelected(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const src = e.target.result;
        const img = new Image();
        img.onload = () => {
          this.cropImage = img;
          this.cropImageSrc = src;
          this.openCropperFor(img);
        };
        img.src = src;
      };
      reader.readAsDataURL(file);
      ev.target.value = '';   // allow re-selecting the same file later
    },
    openCropperFor(img) {
      // Fit the image into a max 480×480 stage, preserving aspect.
      const maxStage = 480;
      const r = Math.min(maxStage / img.naturalWidth, maxStage / img.naturalHeight, 1);
      const dispW = Math.round(img.naturalWidth * r);
      const dispH = Math.round(img.naturalHeight * r);
      this.cropDisplay = { w: dispW, h: dispH, scale: r };

      // Initial crop: 5:6 portrait, ~80% of the smaller dimension, centered.
      const fitH = Math.round(Math.min(dispH * 0.9, (dispW * 0.9) * 6 / 5));
      const fitW = Math.round(fitH * 5 / 6);
      this.cropBox = {
        x: Math.round((dispW - fitW) / 2),
        y: Math.round((dispH - fitH) / 2),
        w: fitW,
        h: fitH,
      };
      this.cropMin = 60;
      this.cropMax = Math.min(dispH, Math.round((dispW) * 6 / 5));
      this.showCropper = true;
      this.$nextTick(() => this.renderCropPreview());
    },
    onCropResize() {
      // Slider drives height; keep 5:6 aspect and clamp inside the stage.
      let h = Math.max(this.cropMin, Math.min(this.cropMax, this.cropBox.h));
      let w = Math.round(h * 5 / 6);
      if (w > this.cropDisplay.w) { w = this.cropDisplay.w; h = Math.round(w * 6 / 5); }
      this.cropBox.w = w;
      this.cropBox.h = h;
      // Re-clamp position
      if (this.cropBox.x + w > this.cropDisplay.w) this.cropBox.x = this.cropDisplay.w - w;
      if (this.cropBox.y + h > this.cropDisplay.h) this.cropBox.y = this.cropDisplay.h - h;
      if (this.cropBox.x < 0) this.cropBox.x = 0;
      if (this.cropBox.y < 0) this.cropBox.y = 0;
      this.renderCropPreview();
    },
    onCropDragStart(e) {
      const stage = this.$refs.cropStage;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const pt = this.cropPoint(e, rect);
      // Only start drag when pointer is inside the frame
      if (pt.x < this.cropBox.x || pt.x > this.cropBox.x + this.cropBox.w
          || pt.y < this.cropBox.y || pt.y > this.cropBox.y + this.cropBox.h) return;
      this.cropDrag = { dx: pt.x - this.cropBox.x, dy: pt.y - this.cropBox.y };
      window.addEventListener('mousemove', this.onCropDragMove);
      window.addEventListener('mouseup', this.onCropDragEnd);
      window.addEventListener('touchmove', this.onCropDragMove, { passive: false });
      window.addEventListener('touchend', this.onCropDragEnd);
      e.preventDefault();
    },
    onCropDragMove(e) {
      if (!this.cropDrag) return;
      const stage = this.$refs.cropStage;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const pt = this.cropPoint(e, rect);
      let nx = pt.x - this.cropDrag.dx;
      let ny = pt.y - this.cropDrag.dy;
      // Clamp inside stage
      nx = Math.max(0, Math.min(this.cropDisplay.w - this.cropBox.w, nx));
      ny = Math.max(0, Math.min(this.cropDisplay.h - this.cropBox.h, ny));
      this.cropBox.x = nx;
      this.cropBox.y = ny;
      this.renderCropPreview();
      if (e.cancelable) e.preventDefault();
    },
    onCropDragEnd() {
      this.cropDrag = null;
      window.removeEventListener('mousemove', this.onCropDragMove);
      window.removeEventListener('mouseup', this.onCropDragEnd);
      window.removeEventListener('touchmove', this.onCropDragMove);
      window.removeEventListener('touchend', this.onCropDragEnd);
    },
    cropPoint(e, rect) {
      const t = (e.touches && e.touches[0]) || e;
      return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    },
    renderCropPreview() {
      const c = this.$refs.cropPreview;
      if (!c || !this.cropImage) return;
      const ctx = c.getContext('2d');
      const s = 1 / this.cropDisplay.scale;
      const sx = this.cropBox.x * s;
      const sy = this.cropBox.y * s;
      const sw = this.cropBox.w * s;
      const sh = this.cropBox.h * s;
      ctx.fillStyle = '#0A1628';
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(this.cropImage, sx, sy, sw, sh, 0, 0, c.width, c.height);
    },
    closeCropper() {
      if (this.photoUploading) return;
      this.onCropDragEnd();
      this.showCropper = false;
      this.cropImage = null;
      this.cropImageSrc = '';
    },
    async acceptCrop() {
      if (!this.cropImage || !this.editForm.studentId) return;
      this.photoUploading = true;
      try {
        // Render the source-pixel crop to a 600×720 JPEG (5:6) — matches existing compression style (q=0.85).
        const out = document.createElement('canvas');
        out.width = 600; out.height = 720;
        const octx = out.getContext('2d');
        const s = 1 / this.cropDisplay.scale;
        octx.fillStyle = '#FFFFFF';
        octx.fillRect(0, 0, out.width, out.height);
        octx.drawImage(this.cropImage,
          this.cropBox.x * s, this.cropBox.y * s, this.cropBox.w * s, this.cropBox.h * s,
          0, 0, out.width, out.height);
        const base64 = out.toDataURL('image/jpeg', 0.85).split(',')[1];

        const r = await executeJson({ function: 'uploadStudentPhoto', studentId: this.editForm.studentId, photoBase64: base64 }, simitrack);
        if (r.data.success) {
          const url = r.data.data && r.data.data.photo_url ? r.data.data.photo_url : '';
          this.editForm.photoUrl = url;
          this.photoVersion++;                                          // bust img cache
          if (this.editStudentRef) this.editStudentRef.photo_url = url; // refresh row preview
          this.editSuccess = `Photo updated (${r.data.data && r.data.data.file_size_kb ? r.data.data.file_size_kb : '?'}KB)`;
          this.closeCropper();
          setTimeout(() => { this.editSuccess = ''; }, 4000);
        } else {
          alert('✗ ' + (r.data.message || 'Upload failed'));
        }
      } catch (e) {
        alert('✗ Upload error');
      }
      this.photoUploading = false;
    },

    async confirmDeleteStudent(s) {
      if (!confirm(`Delete ${s.name} (${s.admission_no})?\n\nThis is permanent if the student has no payments / wallet balance. If they have linked records, you'll be offered to soft-archive instead.`)) return;
      this.deletingId = s.id;
      try {
        var r = await executeJson({ function: 'deleteStudent', studentId: s.id }, simitrack);
        if (r.data.success) {
          this.loadStudents();
        } else {
          // Likely failed because of wallet balance / payment records — offer soft archive
          if (confirm('✗ ' + (r.data.message || 'Cannot hard-delete') + '\n\nSoft-archive instead? (Sets status to "transferred_out" — preserves history)')) {
            var f = await executeJson({ function: 'deleteStudent', studentId: s.id, force: 1 }, simitrack);
            if (f.data.success) { alert('✓ Archived'); this.loadStudents(); }
            else { alert('✗ ' + (f.data.message || 'Archive failed')); }
          }
        }
      } catch (e) { alert('Connection error'); }
      this.deletingId = null;
    },

    async saveStudentEdit() {
      this.editError = ''; this.editSuccess = '';
      if (!this.editForm.name) { this.editError = 'Name is required'; return; }
      if (!this.editForm.admissionNo) { this.editError = 'Admission number is required'; return; }
      this.editProcessing = true;
      try {
        var r = await executeJson({ function: 'updateStudent', ...this.editForm }, simitrack);
        if (r.data.success) {
          this.editSuccess = 'Student updated';
          await this.loadStudents();
          setTimeout(() => { this.showEdit = false; this.editSuccess = ''; }, 1200);
        } else {
          this.editError = r.data.message || 'Update failed';
        }
      } catch (e) { this.editError = 'Connection error'; }
      this.editProcessing = false;
    },

    async loadStudents() {
      this.loading = true;
      try {
        var params = {
          function: 'getStudents',
          status: this.filterStatus,
          page: this.page,
          pageSize: this.pageSize,
        };
        if (this.search) params.search = this.search;
        if (this.filterClass) params.classId = this.filterClass;
        var r = await executeJson(params, simitrack);
        if (r.data.success) {
          this.students = r.data.data.students || [];
          this.total = r.data.data.total || 0;
          this.totalPages = r.data.data.totalPages || 1;
          // If the server clamped us to a different page (e.g. a filter dropped
          // total below the current offset), follow it back.
          if (r.data.data.page && r.data.data.page !== this.page) this.page = r.data.data.page;
        }
      } catch (e) { console.error(e); }
      this.loading = false;
    },
    // Reset to page 1 whenever filters change, then reload
    onFilterChange() { this.page = 1; this.loadStudents(); },
    onPageSizeChange() { this.page = 1; this.loadStudents(); },
    goToPage(p) {
      var max = this.totalPages || 1;
      if (p < 1) p = 1;
      if (p > max) p = max;
      if (p === this.page) return;
      this.page = p;
      this.loadStudents();
    },

    openAddStudent() {
      this.showAdd = true;
      this.addError = '';
      this.form = {
        name: '', admissionNo: '', dateOfBirth: '', gender: '', gradeId: '',
        parents: [{ name: '', phone: '', email: '', relationship: 'father', receivesSms: 1, receivesEmail: 1 }]
      };
    },

    addParentRow() {
      if (this.form.parents.length < 4) {
        this.form.parents.push({ name: '', phone: '', email: '', relationship: 'guardian', receivesSms: 1, receivesEmail: 1 });
      }
    },

    removeParentRow(idx) { this.form.parents.splice(idx, 1); },

    async addStudent(allowDuplicate) {
      this.addError = '';
      this.duplicateMatches = [];
      if (!this.form.name) { this.addError = 'Student name is required'; return; }
      if (!this.form.parents[0].name || !this.form.parents[0].phone) { this.addError = 'At least one parent with name and phone is required'; return; }

      // Validate all parents
      for (var i = 0; i < this.form.parents.length; i++) {
        var p = this.form.parents[i];
        if (p.name && !p.phone) { this.addError = 'Parent ' + (i + 1) + ': Phone is required'; return; }
        if (!p.name && p.phone) { this.addError = 'Parent ' + (i + 1) + ': Name is required'; return; }
      }

      var validParents = this.form.parents.filter(function(p) { return p.name && p.phone; });

      this.processing = true;
      try {
        var r = await executeJson({
          function: 'addStudent',
          name: this.form.name,
          admissionNo: this.form.admissionNo,
          dateOfBirth: this.form.dateOfBirth,
          gender: this.form.gender,
          gradeId: this.form.gradeId,
          parents: JSON.stringify(validParents),
          allowDuplicate: allowDuplicate ? 1 : 0,
        }, simitrack);
        if (r.data.success) {
          this.showAdd = false;
          this.loadStudents();
        } else if (r.data.data && r.data.data.duplicates && r.data.data.duplicates.length) {
          // Server detected possible duplicate(s) — show them and let admin decide
          this.duplicateMatches = r.data.data.duplicates;
          this.duplicateReason = r.data.data.reason || 'name';
          this.addError = r.data.message || 'Possible duplicate detected';
        } else {
          this.addError = r.data.message || 'Failed';
        }
      } catch (e) { this.addError = 'Connection error'; }
      this.processing = false;
    },

    async viewStudent(student) {
      this.viewData = student;
      this.showView = true;
      this.viewParentsLoading = true;
      this.pinMessage = '';
      this.pinSuccess = false;
      this.stmtTransactions = [];
      this.stmtLoaded = false;
      // Check if student has PIN
      try {
        var pr = await executeJson({ function: 'checkStudentPin', studentId: student.id }, simitrack);
        if (pr.data.success) {
          this.viewData.has_pin = pr.data.data?.has_pin || false;
          this.viewData.pin_status = pr.data.data?.status || 'not_set';
        }
      } catch(e) { this.viewData.pin_status = 'not_set'; }
      try {
        var r = await executeJson({ function: 'getStudentParents', studentId: student.id }, simitrack);
        if (r.data.success) this.viewParents = Array.isArray(r.data.data) ? r.data.data : [];
      } catch (e) { this.viewParents = []; }
      this.viewParentsLoading = false;
    },

    async resetPin(student) {
      if (!confirm(`Reset wallet PIN for ${student.name}? A new PIN will be sent via SMS to their parent/student phone.`)) return;
      this.pinResetting = true;
      this.pinMessage = '';
      try {
        var r = await executeJson({ function: 'resetStudentPin', studentId: student.id }, simitrack);
        if (r.data.success) {
          this.pinMessage = r.data.message || 'New PIN sent';
          this.pinSuccess = true;
          this.viewData.has_pin = false;
        } else {
          this.pinMessage = r.data.message || 'Failed to reset PIN';
          this.pinSuccess = false;
        }
      } catch(e) { this.pinMessage = 'Network error'; this.pinSuccess = false; }
      this.pinResetting = false;
    },

    showSetPinDialog(student) {
      const pin = prompt(`Set wallet PIN for ${student.name}\n\nEnter 4-digit PIN:`);
      if (!pin || pin.length < 4) { if (pin !== null) alert('PIN must be at least 4 digits'); return; }
      if (!/^\d+$/.test(pin)) { alert('PIN must be numbers only'); return; }
      this.setPinManually(student, pin);
    },

    async setPinManually(student, pin) {
      try {
        var r = await executeJson({ function: 'adminSetStudentPin', studentId: student.id, pin: pin }, simitrack);
        if (r.data.success) {
          this.pinMessage = 'PIN set successfully';
          this.pinSuccess = true;
          this.viewData.has_pin = true;
          this.viewData.pin_status = 'set';
        } else {
          this.pinMessage = r.data.message || 'Failed to set PIN';
          this.pinSuccess = false;
        }
      } catch(e) { this.pinMessage = 'Network error'; this.pinSuccess = false; }
    },

    async loadStatement(student) {
      if (!student.wallet_id) { this.stmtTransactions = []; this.stmtLoaded = true; return; }
      this.stmtLoading = true;
      try {
        var r = await executeJson({ function: 'getWalletTransactions', walletId: student.wallet_id, limit: 100 }, simitrack);
        if (r.data.success && r.data.data) {
          this.stmtTransactions = Array.isArray(r.data.data) ? r.data.data : (r.data.data.transactions || []);
        }
      } catch(e) { this.stmtTransactions = []; }
      this.stmtLoading = false;
      this.stmtLoaded = true;
    },

    formatDate(d) {
      if (!d) return '-';
      const dt = new Date(d);
      return dt.toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },

    async printStatement(student) {
      try {
        var r = await executeJson({ function: 'getWalletTransactions', walletId: student.wallet_id || '', studentId: student.id }, simitrack);
        var txns = [];
        if (r.data.success && r.data.data) txns = Array.isArray(r.data.data) ? r.data.data : (r.data.data.transactions || []);

        var instName = sessionStorage.getItem('institutionName') || 'School';
        var html = `<!DOCTYPE html><html><head><title>Wallet Statement - ${student.name}</title>
          <style>
            body{font-family:Arial,sans-serif;padding:30px;color:#333;max-width:800px;margin:auto;}
            h2{color:#0D2B5E;margin-bottom:4px;} .sub{color:#666;font-size:13px;margin-bottom:20px;}
            table{width:100%;border-collapse:collapse;margin-top:10px;}
            th{background:#0D2B5E;color:#fff;text-align:left;padding:8px 10px;font-size:12px;}
            td{padding:8px 10px;border-bottom:1px solid #eee;font-size:13px;}
            tr:nth-child(even){background:#f9f9f9;}
            .credit{color:#10B981;font-weight:700;} .debit{color:#EF4444;font-weight:700;}
            .summary{display:flex;gap:30px;margin:16px 0;} .summary div{background:#f0f4ff;padding:12px 20px;border-radius:8px;}
            .summary strong{font-size:18px;display:block;color:#0D2B5E;}
            .footer{margin-top:30px;font-size:11px;color:#999;border-top:1px solid #eee;padding-top:10px;}
            @media print{body{padding:10px;}}
          </style></head><body>
          <h2>${instName}</h2>
          <div class="sub">Wallet Statement for <strong>${student.name}</strong> (${student.admission_no})</div>
          <div class="sub">Generated: ${new Date().toLocaleString()}</div>
          <div class="summary">
            <div>Current Balance<strong style="color:#4FC3F7;">KSh ${this.fNum(student.wallet_balance)}</strong></div>
            <div>Total Transactions<strong>${txns.length}</strong></div>
          </div>
          <table><thead><tr><th>Date</th><th>Description</th><th>Type</th><th>Amount</th><th>Balance</th></tr></thead><tbody>`;

        txns.forEach(t => {
          var type = t.type || t.transaction_type || '';
          var cls = type === 'credit' ? 'credit' : 'debit';
          var sign = type === 'credit' ? '+' : '-';
          html += '<tr><td>' + (t.created_at || t.date || '') + '</td><td>' + (t.description || t.narration || '-') + '</td><td class="' + cls + '">' + type.toUpperCase() + '</td><td class="' + cls + '">' + sign + ' KSh ' + this.fNum(t.amount) + '</td><td>KSh ' + this.fNum(t.balance_after || t.running_balance || '') + '</td></tr>';
        });

        html += '</tbody></table>';
        if (txns.length === 0) html += '<p style="text-align:center;color:#999;padding:40px;">No transactions found</p>';
        html += '<div class="footer">Simitrac — ' + instName + ' | This is a computer-generated statement</div></body></html>';

        var win = window.open('', '_blank');
        win.document.write(html);
        win.document.close();
        win.print();
      } catch(e) { alert('Error loading statement: ' + e.message); }
    },

    openAddParent(student) {
      this.addParentStudent = student;
      this.newParent = { parentName: '', phone: '', email: '', relationship: 'guardian' };
      this.addParentError = '';
      this.showAddParent = true;
    },

    async addParentToStudent() {
      this.addParentError = '';
      if (!this.newParent.parentName || !this.newParent.phone) { this.addParentError = 'Name and phone are required'; return; }
      this.addingParent = true;
      try {
        var r = await executeJson({
          function: 'addParentToStudent',
          studentId: this.addParentStudent.id,
          ...this.newParent
        }, simitrack);
        if (r.data.success) {
          this.showAddParent = false;
          this.loadStudents();
          if (this.showView) this.viewStudent(this.addParentStudent);
        } else { this.addParentError = r.data.message || 'Failed'; }
      } catch (e) { this.addParentError = 'Connection error'; }
      this.addingParent = false;
    },

    async removeParent(parentRecordId) {
      if (!confirm('Remove this parent/guardian?')) return;
      try {
        var r = await executeJson({ function: 'removeParentFromStudent', parentRecordId: parentRecordId }, simitrack);
        if (r.data.success) {
          this.viewParents = this.viewParents.filter(function(p) { return p.id !== parentRecordId; });
        }
      } catch (e) {}
    },

    async resendParentWelcome(p) {
      this.resendingId = p.id;
      try {
        var r = await executeJson({ function: 'resendParentWelcome', parentRecordId: p.id }, simitrack);
        if (r.data.success) {
          alert('✓ ' + (r.data.message || 'Welcome SMS resent.'));
        } else {
          alert('✗ ' + (r.data.message || 'Failed to resend'));
        }
      } catch (e) { alert('Connection error'); }
      this.resendingId = null;
    },

    async openFindDupes() {
      this.showDupes = true;
      this.dupesLoading = true;
      this.dupClusters = [];
      this.dupSelections = {};
      try {
        const r = await executeJson({ function: 'findDuplicateStudents' }, simitrack);
        if (r.data.success) {
          this.dupClusters = (r.data.data && r.data.data.clusters) || [];
          // Pre-select the row with the most "real" data — most parents + payments + attendance
          this.dupClusters.forEach((c, idx) => {
            const best = c.students.slice().sort((a, b) => {
              const score = s => (Number(s.parent_count) * 10) + (Number(s.payment_count) * 5) + (Number(s.attendance_count) > 0 ? 3 : 0) + (s.status === 'active' ? 1 : 0);
              return score(b) - score(a);
            })[0];
            if (best) this.$set ? this.$set(this.dupSelections, idx, best.id) : (this.dupSelections[idx] = best.id);
          });
        }
      } catch (e) { /* silent */ }
      this.dupesLoading = false;
    },
    async resolveDupes() {
      if (!this.allClustersChosen) return;
      const totalToDelete = this.dupClusters.reduce((sum, c, idx) => sum + c.students.filter(s => s.id !== this.dupSelections[idx]).length, 0);
      if (!confirm(`Resolve ${this.dupClusters.length} cluster(s)? ${totalToDelete} duplicate student record(s) will be deleted (or archived if they have payments/wallet balance).`)) return;
      this.dupResolving = true;
      let totalDeleted = 0, totalArchived = 0;
      try {
        for (let i = 0; i < this.dupClusters.length; i++) {
          const cluster = this.dupClusters[i];
          const keep = this.dupSelections[i];
          const deleteIds = cluster.students.filter(s => s.id !== keep).map(s => s.id);
          if (!deleteIds.length) continue;
          const r = await executeJson({
            function: 'resolveDuplicateStudents',
            keepStudentId: keep,
            deleteStudentIds: JSON.stringify(deleteIds),
          }, simitrack);
          if (r.data.success) {
            totalDeleted += (r.data.data.deleted || 0);
            totalArchived += (r.data.data.archived || 0);
          }
        }
        alert(`✓ Done — ${totalDeleted} deleted, ${totalArchived} archived.`);
        this.showDupes = false;
        await this.loadStudents();
      } catch (e) { alert('Connection error during resolution'); }
      this.dupResolving = false;
    },

    async resendAllParentWelcomes() {
      if (!confirm('Resend the updated welcome SMS (with parent-portal link) to EVERY parent in this school? This consumes SMS credits.')) return;
      this.resendingBulk = true;
      try {
        var r = await executeJson({ function: 'resendParentWelcome' }, simitrack);
        if (r.data.success) {
          var d = r.data.data || {};
          alert('✓ Resent welcome SMS to ' + (d.sent || 0) + ' parent(s)' + (d.failed ? ', ' + d.failed + ' failed' : '') + '.');
        } else {
          alert('✗ ' + (r.data.message || 'Failed'));
        }
      } catch (e) { alert('Connection error'); }
      this.resendingBulk = false;
    }
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:24px;margin-bottom:16px;}
.st-table{width:100%;border-collapse:collapse;}
.st-table th{color:#8BA4C4;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:1px;padding:10px 12px;border-bottom:1px solid #1A3A6A;text-align:left;}
.st-table td{color:#E8EDF4;font-size:13px;padding:12px;border-bottom:1px solid #1A3A6A;}
.st-table tr:hover td{background:rgba(46,134,222,.05);}
.st-badge{padding:3px 10px;border-radius:50px;font-size:11px;font-weight:600;}
.st-badge-success{background:rgba(16,185,129,.15);color:#10B981;}
.st-badge-warning{background:rgba(245,158,11,.15);color:#F59E0B;}
.st-badge-danger{background:rgba(239,68,68,.15);color:#EF4444;}
.st-badge-info{background:rgba(46,134,222,.15);color:#4FC3F7;}
.st-btn{border:none;border-radius:8px;padding:8px 16px;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s;}
.st-btn-sm{padding:5px 12px;font-size:12px;}
.st-btn-blue{background:rgba(46,134,222,.15);color:#4FC3F7;border:1px solid rgba(46,134,222,.3);}
.st-btn-blue:hover{background:rgba(46,134,222,.25);}
.st-btn-green{background:rgba(16,185,129,.15);color:#10B981;border:1px solid rgba(16,185,129,.3);}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:10px;padding:12px;font-size:15px;}
.st-btn-primary:disabled{opacity:.6;}
.st-btn-icon{background:none;border:none;cursor:pointer;padding:2px;color:#8BA4C4;font-size:18px;}.st-btn-icon:hover{color:#EF4444;}
.st-empty{text-align:center;padding:40px 20px;color:#8BA4C4;}.st-empty i{font-size:48px;color:#1A3A6A;display:block;margin-bottom:12px;}
.st-spinner{display:flex;justify-content:center;padding:40px;}.st-spinner .spinner-border{color:#2E86DE;}
.st-search-wrap{position:relative;}.st-search-wrap i{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#8BA4C4;}
.st-search{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:9px 14px 9px 36px;color:#E8EDF4;font-size:14px;outline:none;width:100%;max-width:300px;}
.st-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px;}
.st-modal{background:#0F2347;border:1px solid #1A3A6A;border-radius:16px;padding:28px;width:100%;max-width:500px;max-height:90vh;overflow-y:auto;}
.st-modal-title{color:#E8EDF4;font-size:18px;font-weight:700;margin-bottom:20px;display:flex;align-items:center;justify-content:space-between;}
.st-modal-close{color:#8BA4C4;cursor:pointer;font-size:22px;background:none;border:none;}
.st-label{color:#8BA4C4;font-size:13px;font-weight:600;margin-bottom:6px;display:block;}
.st-input,.st-select{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:10px 14px;color:#E8EDF4;font-size:14px;outline:none;transition:border .2s;}
.st-input:focus,.st-select:focus{border-color:#2E86DE;}
.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;margin-bottom:16px;}
.st-alert-danger{background:rgba(255,107,107,.1);border:1px solid rgba(255,107,107,.2);color:#FF9999;}
.st-section-label{color:#4FC3F7;font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;}

/* Parent Card in Add Form */
.st-parent-card{background:#0A1628;border:1px solid #1A3A6A;border-radius:12px;padding:16px;margin-bottom:10px;}
.st-parent-label{color:#E8EDF4;font-size:13px;font-weight:600;}
.st-check-inline{display:flex;align-items:center;gap:6px;color:#8BA4C4;font-size:12px;cursor:pointer;}
.st-check-inline input{accent-color:#2E86DE;}

/* Parent View Card */
.st-parent-view-card{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:14px;margin-bottom:8px;}
.st-parent-view-name{color:#E8EDF4;font-size:14px;font-weight:700;margin-bottom:6px;}
.st-parent-view-detail{color:#8BA4C4;font-size:12px;margin-bottom:3px;}

/* Detail View */
.st-detail-label{color:#8BA4C4;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:1px;}
.st-detail-val{color:#E8EDF4;font-size:14px;font-weight:500;margin-top:2px;}

/* Date Picker Dark Theme Fix */
.st-date-input{position:relative;cursor:pointer;}
.st-date-input::-webkit-calendar-picker-indicator{filter:invert(0.7);cursor:pointer;padding:4px;margin-right:-4px;}
.st-date-input::-webkit-datetime-edit{color:#E8EDF4;}
.st-date-input::-webkit-datetime-edit-fields-wrapper{color:#E8EDF4;}
.st-date-input::-webkit-datetime-edit-text{color:#4A6B8A;}
.st-date-input::-webkit-datetime-edit-month-field,
.st-date-input::-webkit-datetime-edit-day-field,
.st-date-input::-webkit-datetime-edit-year-field{color:#E8EDF4;}
.st-date-input::-webkit-datetime-edit-month-field:focus,
.st-date-input::-webkit-datetime-edit-day-field:focus,
.st-date-input::-webkit-datetime-edit-year-field:focus{background:rgba(46,134,222,.2);color:#4FC3F7;border-radius:2px;outline:none;}

/* ========== Student row avatar ========== */
.st-avatar{width:40px;height:48px;border-radius:6px;overflow:hidden;background:#0F2347;border:1px solid #1A3A6A;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:.15s;}
.st-avatar:hover{border-color:#4FC3F7;}
.st-avatar img{width:100%;height:100%;object-fit:cover;display:block;}
.st-avatar-fallback{color:#4FC3F7;font-weight:700;font-size:13px;letter-spacing:.5px;}
.st-avatar-lg{width:80px;height:96px;border-radius:8px;border-width:2px;}
.st-avatar-lg .st-avatar-fallback{font-size:24px;}

/* ========== Photo cropper ========== */
.crop-stage{position:relative;background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;overflow:hidden;cursor:grab;user-select:none;flex-shrink:0;max-width:100%;}
.crop-stage img{display:block;pointer-events:none;}
.crop-frame{position:absolute;border:2px solid #4FC3F7;cursor:move;
  /* The huge box-shadow darkens everything outside the frame, giving a clean "lit crop" effect without a separate mask layer. */
  box-shadow:0 0 0 9999px rgba(10,22,40,.72), 0 0 0 1px rgba(0,0,0,.6) inset;
}
.crop-frame-grid{position:absolute;inset:0;pointer-events:none;
  background:
    linear-gradient(to right, transparent 33.3%, rgba(255,255,255,.3) 33.3%, rgba(255,255,255,.3) 33.5%, transparent 33.5%, transparent 66.6%, rgba(255,255,255,.3) 66.6%, rgba(255,255,255,.3) 66.8%, transparent 66.8%),
    linear-gradient(to bottom, transparent 33.3%, rgba(255,255,255,.3) 33.3%, rgba(255,255,255,.3) 33.5%, transparent 33.5%, transparent 66.6%, rgba(255,255,255,.3) 66.6%, rgba(255,255,255,.3) 66.8%, transparent 66.8%);
}
</style>